import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import { CbhIcon, DotSeparatedList, Pill, Text } from "@src/appV2/redesign/system/ui-components";
import { differenceInSeconds } from "date-fns";
import { useEffect } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { ShiftCardWrapper } from "../../Shift/Card/Wrapper";
import { formatToHoursAndMinutes } from "../../utils/formatToHoursAndMinutes";
import { VisitCardContentWrapper } from "../Card/ContentWrapper";
import { VisitPayInfoLabel } from "../Card/PayInfoLabel";
import { readableVisitTypeMapping, VisitPricingTypes, VisitTypes } from "../constants";
import { BOOKED_VISIT_DETAILS_FULL_PATH } from "../paths";
import { type PatientData, type VisitWithCaseData } from "../types";
import { getHighlightedVisitActionDeadlineTime } from "../utils";

interface InHomeHighlightedCardContentProps {
  visit: VisitWithCaseData;
  patient: PatientData | undefined;
}

function getHighlightCardTitle(visit: VisitWithCaseData, patient: PatientData) {
  const { pricingType, type, estimatedStartTime } = visit.attributes;
  const { externalPatientIdentifier } = patient.attributes;

  if (type !== VisitTypes.REGULAR) {
    const readableType = readableVisitTypeMapping[type] ?? type;
    return `Prepare for your ${readableType.toLowerCase()} visit for ${externalPatientIdentifier}`;
  }

  if (pricingType === VisitPricingTypes.PER_VISIT) {
    return `Prepare for your in-home visit for ${externalPatientIdentifier}`;
  }

  // We expect estimatedStartTime to always exist but if not then we default to noon
  return `Prepare for your in-home visit for ${externalPatientIdentifier} at ${
    estimatedStartTime ?? "12:00 PM"
  }`;
}

export function InHomeHighlightedCardContent(props: InHomeHighlightedCardContentProps) {
  const { visit, patient } = props;
  const actionDeadlineTime = getHighlightedVisitActionDeadlineTime(visit);
  const countdownInSeconds = isDefined(actionDeadlineTime)
    ? differenceInSeconds(actionDeadlineTime, new Date())
    : 0;

  const { secondsLeft, startTimer } = useCountdownTimer({ durationInSeconds: countdownInSeconds });
  const history = useHistory();

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  if (!isDefined(visit) || !isDefined(patient)) {
    return null;
  }

  return (
    <ShiftCardWrapper variant="tertiary" outlined={false} sx={{ width: "100%" }}>
      <VisitCardContentWrapper
        sx={(theme) => ({
          background: theme.palette.homeHealth?.visit.gradient,
        })}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Pill
            size="medium"
            label={formatToHoursAndMinutes(secondsLeft)}
            variant="icon-highlight"
          />
          <CbhIcon type="house-filled" size="medium" />
        </Stack>

        <Stack direction="column" spacing={5} marginTop={12}>
          <Text semibold variant="h5">
            {getHighlightCardTitle(visit, patient)}
          </Text>

          <DotSeparatedList sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <Text variant="body2">
              {readableVisitTypeMapping[visit.attributes.type] ?? visit.attributes.type}{" "}
              {visit.attributes.type !== VisitTypes.REGULAR && "visit"}
            </Text>
            <VisitPayInfoLabel visit={visit} wrapperSx={{ width: "50%" }} showTotalPay={false} />
          </DotSeparatedList>

          <Button
            invert
            variant="contained"
            onClick={() => {
              history.push(
                generatePath(BOOKED_VISIT_DETAILS_FULL_PATH, {
                  caseId: visit.attributes.caseId,
                  visitId: visit.id,
                })
              );
            }}
          >
            View details and log visit
          </Button>
        </Stack>
      </VisitCardContentWrapper>
    </ShiftCardWrapper>
  );
}
